// Model and provider settings. Backed by
// pi-ai's model catalog (via src/lib/modelCatalog.js) — the curated
// short-list of CORS-friendly providers, with each provider's
// models ranked into Recommended / Strong / Other tiers so the
// "just pick something good" path lands on a Sonnet-4.6-class
// model by default.
//
// Provider + API key persist via PUT /v1/tenant/configuration's
// `llmProvider` block (encrypted server-side, never returned to the
// browser). Task model is a per-browser preference and stays in
// localStorage.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getChatCreds, setChatCreds } from "../lib/chatCreds.js";
import { MODEL_TIERS, defaultModelFor, defaultTaskModelFor, findCuratedProvider, getRankedModels, lookupCatalogModel } from "../lib/modelCatalog.js";

// Providers the backend tenant LLM proxy supports. The first three carry
// hardcoded upstream URLs; "custom" is the BYO-gateway option — the admin
// supplies a base URL + host allowlist and the proxy forwards there (host
// allowlist + private-range SSRF backstop enforced server-side, see the
// backend `llm_proxy` / `llm_custom_host`).
const SUPPORTED_PROVIDERS = ["anthropic", "openrouter", "openai", "custom"];
import Select from "./Select.jsx";
import ChatModelList, { IN_USE_TIER } from "./ChatModelList.jsx";
import { initialChatTab } from "./chatModelPickerTabs.js";
import Button from "./ui/Button.jsx";
import { SkeletonRows } from "./ui/Skeleton.jsx";
import Tabs from "./ui/Tabs.jsx";

// Group ranked models by tier in display order. Empty tiers are
// omitted so the list doesn't end with a dangling header. The models in
// `pinnedIds` (chat's, then the task model) that the list holds are pulled
// out into an "In use" group at the top, so reopening the picker shows what
// is active first.
function groupByTier(ranked, pinnedIds) {
    const groups = [];
    const pinned = [...new Set(pinnedIds.filter(Boolean))]
        .map((id) => ranked.find((r) => r.model.id === id))
        .filter(Boolean);
    if (pinned.length) {
        groups.push({
            tier: IN_USE_TIER,
            items: pinned.map((r) => ({ model: r.model, tier: r.tier, isDefault: r.isDefault })),
        });
    }
    const out = new Set(pinned.map((r) => r.model.id));
    const order = [MODEL_TIERS.REASONING, MODEL_TIERS.OTHER];
    for (const tier of order) {
        const items = ranked.filter((r) => r.tier === tier && !out.has(r.model.id));
        if (items.length) groups.push({ tier, items });
    }
    return groups;
}

// The picker inside chat's settings dialog (settings/ChatSettingsDialog.jsx):
// the Model tab (ChatModelList.jsx) and the Provider and key tab.
export default function ChatModelPicker({ onClose, onSaved, tab = null }) {
    const [tenantId, setTenantId] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [provider, setProvider] = useState(SUPPORTED_PROVIDERS[0]);
    const [modelId, setModelId] = useState("");
    // Snapshot of the model id at popover-open time. Drives the
    // "Current" pinned row at the top of the model list and stays
    // stable for the lifetime of this picker instance — clicking a
    // different row doesn't move the pin until the picker is
    // closed and reopened.
    const [savedModelId, setSavedModelId] = useState("");
    // The provider that is currently persisted (empty until configured). A
    // provider change must carry a fresh key — the stored key belongs to the
    // previously selected provider — so commit() compares against this.
    const [savedProvider, setSavedProvider] = useState("");
    // Task model — optional cheap/fast model used by sub-agents
    // (kernel extractor, judges). Empty string = "use provider auto-
    // default", which the runtime resolves via defaultTaskModelFor.
    const [taskModelId, setTaskModelId] = useState("");
    // `apiKey` is the user-typed *new* value about to be saved (or
    // "" when they haven't typed anything). The backend never returns
    // the stored key, so an empty value with `hasApiKey=true` means
    // "leave the saved key untouched"; empty with `hasApiKey=false`
    // means "no key on file yet."
    const [apiKey, setApiKey] = useState("");
    const [hasApiKey, setHasApiKey] = useState(false);
    // True when this tenant is served chat on Origin's managed key
    // (no bring-your-own key configured). Chat works with no key; an admin can
    // still save a key to bring their own provider.
    const [managedDefault, setManagedDefault] = useState(false);
    const [revealKey, setRevealKey] = useState(false);
    // Custom (BYO-gateway) provider fields — only used when provider==="custom".
    // `baseUrl` is the OpenAI-compatible gateway URL; `allowlist` is a
    // newline-separated host list the backend proxy enforces (SSRF guard);
    // `authStyle` is the auth header the gateway expects ("bearer" for
    // OpenAI-style, "api-key" for Azure OpenAI).
    const [baseUrl, setBaseUrl] = useState("");
    const [allowlist, setAllowlist] = useState("");
    const [authStyle, setAuthStyle] = useState("bearer");
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Configured tenants open the model list; first setup opens the provider form.
    // Settings opens the picker on one tab with its strip hidden: Model and
    // Provider and key are sections of their own there.
    const [activeTab, setActiveTab] = useState(tab || "model");
    const searchRef = useRef(null);

    // Initial load: pull saved provider/model + has_api_key from the
    // backend. The picker never receives the actual key value — only
    // a flag, mirrored into the field placeholder below.
    useEffect(() => {
        let cancelled = false;
        getChatCreds()
            .then((creds) => {
                if (cancelled) return;
                setTenantId(creds.tenantId);
                setIsAdmin(Boolean(creds.isAdmin));
                setHasApiKey(Boolean(creds.hasApiKey));
                setManagedDefault(Boolean(creds.managedDefault));
                const initialProvider = SUPPORTED_PROVIDERS.includes(creds.provider)
                    ? creds.provider
                    : SUPPORTED_PROVIDERS[0];
                const initialModel = creds.model || defaultModelFor(initialProvider);
                setProvider(initialProvider);
                setModelId(initialModel);
                setSavedModelId(creds.model || "");
                setSavedProvider(creds.provider || "");
                setTaskModelId(creds.taskModel || "");
                setBaseUrl(creds.baseUrl || "");
                setAllowlist(creds.customHostAllowlist || "");
                setAuthStyle(creds.customAuthStyle || "bearer");
                // See initialChatTab: first-time setup opens the provider/key
                // form; managed-default tenants already have working chat, so
                // they land on the model picker rather than being forced into
                // key config. A tab asked for by name stays open.
                if (!tab) setActiveTab(initialChatTab(creds));
            })
            .catch((err) => {
                if (!cancelled) setError(err.message || String(err));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    // Switching provider: snap the model to the new provider's
    // recommended default. Skips the snap when the user already has
    // a model that exists in the new provider's catalog (the same
    // OpenRouter id is sometimes valid across providers). Same logic
    // for the task model — clear it if the saved id isn't in the new
    // catalog, so the auto-default kicks in for the new provider.
    const onProviderChange = useCallback((next) => {
        setProvider(next);
        // Custom has no catalog grid — keep the user on the provider form,
        // where the URL / model / allowlist fields live.
        if (next === "custom") setActiveTab("provider");
        const ranked = getRankedModels(next);
        const stillValid = ranked.some((r) => r.model.id === modelId);
        if (!stillValid) {
            setModelId(defaultModelFor(next));
        }
        if (taskModelId && !ranked.some((r) => r.model.id === taskModelId)) {
            setTaskModelId("");
        }
        setSearch("");
    }, [modelId, taskModelId]);

    const ranked = useMemo(() => getRankedModels(provider), [provider]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return ranked;
        return ranked.filter(({ model }) =>
            model.id.toLowerCase().includes(q) || model.name.toLowerCase().includes(q));
    }, [ranked, search]);


    // Effective task model for display. `taskModelId` is the user
    // override; when empty we fall back to the provider's auto-
    // default so the picker always shows what sub-agents will
    // actually use.
    const effectiveTaskModelId = taskModelId || defaultTaskModelFor(provider);
    const effectiveTaskModel = useMemo(
        () => lookupCatalogModel(provider, effectiveTaskModelId),
        [provider, effectiveTaskModelId],
    );
    const taskIsAuto = !taskModelId;
    const groups = useMemo(() => groupByTier(filtered, [modelId, effectiveTaskModelId]), [filtered, modelId, effectiveTaskModelId]);

    // Persist current form values. `overrideModel` lets the model
    // row click commit a new selection without waiting for setState
    // — clicking a row should save immediately, the explicit Save
    // button only matters when only the provider or api key has
    // changed.
    const commit = useCallback(async (overrideModel) => {
        const finalModel = (overrideModel ?? modelId).trim();
        if (!tenantId) {
            setError("Sign in to an active tenant and try again.");
            return;
        }
        if (!isAdmin) {
            setError("Only tenant admins can change LLM settings. Ask an admin for help.");
            return;
        }
        if (!finalModel) {
            setError("Pick a model.");
            return;
        }
        // Custom (BYO-gateway) provider needs a base URL + at least one
        // allowed host before it can save — the backend rejects custom
        // without both; validate client-side for an immediate message.
        if (provider === "custom") {
            if (!baseUrl.trim()) {
                setError("Base URL is required for a custom provider.");
                return;
            }
            if (!allowlist.trim()) {
                setError("Add at least one allowed host for a custom provider.");
                return;
            }
        }
        // A key is required on first save AND whenever the provider changes —
        // the stored key belongs to the previously selected provider, and
        // reusing it would send one vendor's key to another's endpoint (the
        // backend rejects this too). A same-provider re-save may submit blank
        // to mean "leave the stored key alone".
        const trimmedKey = apiKey.trim();
        const providerChanged = provider !== savedProvider;
        // A managed-default tenant is served on Origin's key, so it needs no
        // bring-your-own key to save a model on its (unchanged) managed
        // provider — which is exactly the flow initialChatTab lands them in
        // (Model tab, no key). Switching to a different provider means going
        // bring-your-own, so a key is required again there.
        const managedNoKeyNeeded = managedDefault && !providerChanged;
        if ((!hasApiKey || providerChanged) && !trimmedKey && !managedNoKeyNeeded) {
            setError(
                providerChanged && hasApiKey
                    ? "Enter an API key for the newly selected provider."
                    : "API key is required for this tenant's first save.",
            );
            return;
        }

        setSaving(true);
        setError(null);
        try {
            // Field-level omit semantics: only send `api_key` when the
            // user actually typed something. Sending `""` clears the
            // stored key on the server — never what we want from this
            // picker (use the Apply path with an explicit clear-key
            // affordance if/when we add one).
            const payload = { provider, model: finalModel };
            if (trimmedKey) payload.apiKey = trimmedKey;
            if (provider === "custom") {
                payload.baseUrl = baseUrl.trim();
                payload.customHostAllowlist = allowlist.trim();
                payload.customAuthStyle = authStyle || "bearer";
            }
            await setChatCreds(payload);
            setApiKey("");
            setHasApiKey(true);
            setSavedModelId(finalModel);
            setSavedProvider(provider);
            await onSaved?.();
            onClose?.();
        } catch (err) {
            setError(err.message || String(err));
        } finally {
            setSaving(false);
        }
    }, [tenantId, isAdmin, apiKey, hasApiKey, provider, savedProvider, modelId, baseUrl, allowlist, authStyle, onSaved, onClose]);

    const onPickModel = useCallback((id) => {
        setModelId(id);
        commit(id);
    }, [commit]);

    // Task model commits independently — the user is just picking
    // which cheap model judges/extractors use, not touching the
    // tenant-shared primary/key. Empty string clears the override and
    // falls back to the provider's auto-default at runtime.
    const onPickTaskModel = useCallback(async (id) => {
        const next = id === taskModelId ? "" : id;
        setTaskModelId(next);
        try {
            await setChatCreds({ taskModel: next });
            await onSaved?.();
        } catch (err) {
            setError(err.message || String(err));
        }
    }, [taskModelId, onSaved]);

    // Custom (BYO-gateway) has no model catalog — its model is free text on
    // the provider form — so the Model tab is hidden for it.
    const showModelTab = provider !== "custom";
    const effectiveTab = !showModelTab && activeTab === "model" ? "provider" : activeTab;

    const tabItems = [
        ...(showModelTab ? [{ id: "model", label: "Model" }] : []),
        { id: "provider", label: "Provider & API key" },
    ];

    // The Model tab is for finding a model: its search has the focus whenever
    // the tab shows, on opening or on switching to it.
    useEffect(() => {
        if (!loading && effectiveTab === "model") searchRef.current?.focus({ preventScroll: true });
    }, [loading, effectiveTab]);

    return (
        <div
            role="region"
            aria-label="LLM configuration"
            aria-busy={loading || saving}
            onClick={(e) => e.stopPropagation()}
            className="flex min-h-0 flex-col tracking-normal normal-case"
        >
            <div className="flex shrink-0 items-center px-[18px] pb-[12px]">
                <Tabs label="LLM configuration" items={tabItems} value={effectiveTab} onChange={setActiveTab} />
            </div>

            {loading ? (
                // Until the saved settings arrive, no provider or model is shown as
                // chosen: the defaults would read as the tenant's and then jump.
                <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-[10px]">
                    <SkeletonRows rows={7} cells={["w-[52%]", "w-[16%]"]} rowClassName="grid h-[34px] grid-cols-[minmax(0,1fr)_auto] items-center gap-[16px]" label="Loading chat settings…" />
                </div>
            ) : effectiveTab === "model" && showModelTab ? (
                // The model list is long, so its tab holds a set height; the
                // provider form is short, and the dialog fits it.
                <div className="flex h-[min(480px,56vh)] min-h-0 flex-col">
                <ChatModelList
                    searchRef={searchRef}
                    search={search}
                    onSearch={setSearch}
                    groups={groups}
                    chatModelId={modelId}
                    taskModelId={effectiveTaskModelId}
                    taskIsAuto={taskIsAuto}
                    onUse={onPickModel}
                    onUseForTasks={onPickTaskModel}
                    disabled={saving}
                />
                </div>
            ) : (
                <ProviderTab
                    isAdmin={isAdmin}
                    provider={provider}
                    onProviderChange={onProviderChange}
                    apiKey={apiKey}
                    onApiKeyChange={setApiKey}
                    hasApiKey={hasApiKey}
                    managedDefault={managedDefault}
                    revealKey={revealKey}
                    onToggleReveal={() => setRevealKey((v) => !v)}
                    baseUrl={baseUrl}
                    onBaseUrlChange={setBaseUrl}
                    allowlist={allowlist}
                    onAllowlistChange={setAllowlist}
                    authStyle={authStyle}
                    onAuthStyleChange={setAuthStyle}
                    model={modelId}
                    onModelChange={setModelId}
                    saving={saving}
                    onApply={() => commit()}
                />
            )}

            {error ? (
                <p role="alert" className="m-0 shrink-0 border-t border-[color:var(--line)] px-[18px] py-[9px] text-[12.5px] text-[color:var(--red)]">{error}</p>
            ) : null}
        </div>
    );
}

function ProviderTab({
    isAdmin,
    provider,
    onProviderChange,
    apiKey,
    onApiKeyChange,
    hasApiKey,
    managedDefault,
    revealKey,
    onToggleReveal,
    baseUrl,
    onBaseUrlChange,
    allowlist,
    onAllowlistChange,
    authStyle,
    onAuthStyleChange,
    model,
    onModelChange,
    saving,
    onApply,
}) {
    // The reveal toggle is only meaningful when the field has a value
    // the user can actually look at. The stored key is never returned
    // to the browser, so an empty input has nothing to reveal.
    const canReveal = apiKey.length > 0;
    const providerLabel = findCuratedProvider(provider)?.label || provider;
    if (!isAdmin) {
        return (
            <div className="grid content-start gap-[10px] px-[18px] pb-[18px] pt-[4px] text-[13px] leading-[1.5] text-[color:var(--ink)]">
                <p className="m-0">
                    The chat LLM is configured by a tenant admin and shared across your tenant.
                </p>
                <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-[12px] gap-y-[6px] text-[12.5px]">
                    <span className="text-[color:var(--muted)]">Provider</span>
                    <span className="text-[color:var(--ink)]">{providerLabel || "(unset)"}</span>
                    <span className="text-[color:var(--muted)]">API key</span>
                    <span className="text-[color:var(--ink)]">
                        {hasApiKey
                            ? "Configured"
                            : managedDefault
                              ? "Managed by Origin. No key required."
                              : "Not configured. Ask your admin."}
                    </span>
                </div>
            </div>
        );
    }
    return (
        <div className="grid min-h-0 flex-1 content-start grid-cols-[96px_minmax(0,1fr)] items-center gap-x-[12px] gap-y-[10px] overflow-y-auto px-[18px] pb-[18px] pt-[4px]">
            {managedDefault && !hasApiKey ? (
                <p className="col-span-2 m-0 rounded-[8px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[11px] py-[9px] text-[12.5px] leading-[1.5] text-[color:var(--muted)]">
                    Chat is using <span className="text-[color:var(--ink)]">Origin-managed inference</span>. No key is required. Add a provider key below to use your own account.
                </p>
            ) : null}
            <span className="text-[12px] text-[color:var(--muted)]">
                Provider
            </span>
            <Select
                value={provider}
                onChange={onProviderChange}
                options={SUPPORTED_PROVIDERS.map((id) => ({
                    value: id,
                    label: findCuratedProvider(id)?.label || id,
                }))}
                variant="field"
                ariaLabel="LLM provider"
            />

            {provider === "custom" ? (
                <>
                    <span className="text-[12px] text-[color:var(--muted)]">
                        Base URL
                    </span>
                    <div className="grid gap-[4px]">
                        <input
                            type="url"
                            value={baseUrl}
                            onChange={(e) => onBaseUrlChange(e.target.value)}
                            placeholder="https://gateway.example.com/v1"
                            className="h-[32px] w-full rounded-[7px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] font-mono text-[12.5px] text-[color:var(--ink)] placeholder:text-[color:var(--muted-soft)] outline-none focus:border-[color:var(--green)]"
                        />
                        <span className="text-[11.5px] leading-[1.45] text-[color:var(--muted-soft)]">
                            Enter the base URL, such as <code>https://gateway.example.com/v1</code>.
                            Origin posts to <code>{"{base}/chat/completions"}</code>.
                            Azure OpenAI query strings such as <code>?api-version=…</code> are preserved.
                        </span>
                    </div>

                    <span className="text-[12px] text-[color:var(--muted)]">
                        Model
                    </span>
                    <input
                        type="text"
                        value={model}
                        onChange={(e) => onModelChange(e.target.value)}
                        placeholder="model id your gateway expects"
                        className="h-[32px] w-full rounded-[7px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] font-mono text-[12.5px] text-[color:var(--ink)] placeholder:text-[color:var(--muted-soft)] outline-none focus:border-[color:var(--green)]"
                    />

                    <span className="text-[12px] text-[color:var(--muted)]">
                        Allowed hosts
                    </span>
                    <div className="grid gap-[4px]">
                        <textarea
                            value={allowlist}
                            onChange={(e) => onAllowlistChange(e.target.value)}
                            rows={2}
                            placeholder="gateway.example.com"
                            className="w-full resize-y rounded-[7px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[7px] font-mono text-[12.5px] leading-[1.35] text-[color:var(--ink)] placeholder:text-[color:var(--muted-soft)] outline-none focus:border-[color:var(--green)]"
                        />
                        <span className="text-[11.5px] leading-[1.45] text-[color:var(--muted-soft)]">
                            Enter one host per line. Origin forwards only to these hosts and rejects private or link-local addresses.
                        </span>
                    </div>

                    <span className="text-[12px] text-[color:var(--muted)]">
                        Auth header
                    </span>
                    <div className="grid gap-[4px]">
                        <Select
                            value={authStyle || "bearer"}
                            onChange={onAuthStyleChange}
                            options={[
                                { value: "bearer", label: "Authorization: Bearer (OpenAI-style)" },
                                { value: "api-key", label: "api-key (Azure OpenAI)" },
                            ]}
                            variant="field"
                            ariaLabel="Custom gateway auth header"
                        />
                        <span className="text-[11.5px] leading-[1.45] text-[color:var(--muted-soft)]">
                            Choose the key authentication method. Azure OpenAI uses <code>api-key</code>. Most gateways use <code>Bearer</code>.
                        </span>
                    </div>
                </>
            ) : null}

            <span className="text-[12px] text-[color:var(--muted)]">
                API key
            </span>
            <div className={canReveal ? "grid gap-[6px] [grid-template-columns:minmax(0,1fr)_auto]" : ""}>
                <input
                    type={revealKey ? "text" : "password"}
                    value={apiKey}
                    onChange={(e) => onApiKeyChange(e.target.value)}
                    autoComplete="new-password"
                    // Backend never returns the stored key — the field
                    // starts blank with a placeholder that reflects
                    // whether one is already on file. Typing replaces;
                    // leaving blank on save keeps the existing key.
                    placeholder={hasApiKey ? "Saved. Type to replace." : "sk-…"}
                    className="h-[32px] w-full rounded-[7px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] font-mono text-[12.5px] text-[color:var(--ink)] placeholder:text-[color:var(--muted-soft)] outline-none focus:border-[color:var(--green)]"
                />
                {canReveal ? (
                    <Button onClick={onToggleReveal}>
                        {revealKey ? "Hide" : "Show"}
                    </Button>
                ) : null}
            </div>

            <span />
            <div className="text-[11.5px] leading-[1.45] text-[color:var(--muted-soft)]">
                    The Origin backend stores this key for your tenant. The browser receives only its configured status.
            </div>

            <span />
            <div className="flex justify-end">
                <Button variant="primary" onClick={onApply} disabled={saving}>
                    {saving ? "Saving…" : "Save"}
                </Button>
            </div>
        </div>
    );
}
